import React, { useState } from 'react';

import { DeleteOutlined } from '@ant-design/icons';
import { Descriptions, Drawer, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { usePagination } from '@/shared/hooks/usePagination';
import { PERFORMANCE_TYPE_CONFIG, PERFORMANCE_UI } from '@/shared/lib/constants/performance';
import { formatBytes } from '@/shared/lib/utils/formatBytes';
import { getStatusColor } from '@/shared/lib/utils/getStatusColor';
import { Button } from '@/shared/ui/Button';

import type { PerformanceLog } from '@/entities/performance';

import styles from './PerformanceLogList.module.css';

type PerformanceLogListProps = {
  logs: PerformanceLog[];
  onClear: () => void;
  selectedType: string | null;
  onFilterChange: (type: string | null) => void;
};

export const PerformanceLogList: React.FC<PerformanceLogListProps> = ({
  logs,
  onClear,
  selectedType,
  onFilterChange,
}) => {
  const [selectedLog, setSelectedLog] = useState<PerformanceLog | null>(null);

  const filteredLogs = selectedType ? logs.filter((log) => log.type === selectedType) : logs;

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: filteredLogs.length,
    viewportOffset: PERFORMANCE_UI.VIEWPORT_OFFSET,
    initialPage: 1,
    initialPageSize: 20,
  });

  const columns: ColumnsType<PerformanceLog> = [
    {
      title: '시간',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 220,
      render: (timestamp: number) => new Date(timestamp).toLocaleString('ko-KR'),
      sorter: (a, b) => a.timestamp - b.timestamp,
      defaultSortOrder: 'descend',
    },
    {
      title: '타입',
      dataIndex: 'type',
      key: 'type',
      width: 160,
      render: (type: string) => {
        const config = PERFORMANCE_TYPE_CONFIG[type as keyof typeof PERFORMANCE_TYPE_CONFIG];
        if (!config) return <Tag>{type}</Tag>;
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      filters: [
        { text: PERFORMANCE_TYPE_CONFIG.api_response_time.label, value: 'api_response_time' },
        { text: PERFORMANCE_TYPE_CONFIG.slow_query.label, value: 'slow_query' },
        { text: PERFORMANCE_TYPE_CONFIG.timeout.label, value: 'timeout' },
        { text: PERFORMANCE_TYPE_CONFIG.connection_reset_retry.label, value: 'connection_reset_retry' },
      ],
      filteredValue: selectedType ? [selectedType] : null,
      filterMultiple: false,
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'URL',
      dataIndex: ['data', 'url'],
      key: 'url',
      ellipsis: true,
      render: (url: string) => {
        if (!url) return '-';
        const parts = url.split('?');
        return (
          <span className={styles.urlText} title={url}>
            {parts[0]}
          </span>
        );
      },
    },
    {
      title: '응답시간',
      dataIndex: ['data', 'duration'],
      key: 'duration',
      width: 120,
      render: (duration: number) => (duration ? `${Math.round(duration)}ms` : '-'),
      sorter: (a, b) => ((a.data.duration as number) || 0) - ((b.data.duration as number) || 0),
    },
    {
      title: '응답 크기',
      dataIndex: ['data', 'responseSize'],
      key: 'responseSize',
      width: 110,
      render: (size: number | undefined) => (size ? formatBytes(size) : '-'),
      sorter: (a, b) => ((a.data.responseSize as number) || 0) - ((b.data.responseSize as number) || 0),
    },
    {
      title: '상태',
      dataIndex: ['data', 'status'],
      key: 'status',
      width: 100,
      render: (status: number | string, record: PerformanceLog) => {
        if (record.type === 'timeout') {
          return <Tag color="volcano">Timeout</Tag>;
        }
        if (record.type === 'slow_query') {
          return <Tag color="gold">200 (Slow)</Tag>;
        }
        if (!status) {
          return record.type === 'connection_reset_retry' ? <Tag color="orange">Connection Reset</Tag> : <span>-</span>;
        }

        return <Tag color={getStatusColor(status)}>{status}</Tag>;
      },
    },
  ];

  return (
    <>
      <div className={styles.listContainer}>
        <div className={styles.header}>
          <h3>
            에러 로그 ({filteredLogs.length}건)
            <Space size={[4, 6]} wrap className={styles.filterTags}>
              {!selectedType && <span className={styles.filterBadge}>전체</span>}
              {selectedType && (
                <span className={styles.filterBadge}>
                  {PERFORMANCE_TYPE_CONFIG[selectedType as keyof typeof PERFORMANCE_TYPE_CONFIG]?.label || selectedType}
                </span>
              )}
            </Space>
          </h3>
          <Space>
            <Button variant="critical" prefix={<DeleteOutlined />} onClick={onClear}>
              전체 삭제
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredLogs}
          size="small"
          rowKey="id"
          pagination={paginationConfig}
          scroll={scrollConfig}
          onRow={(record) => ({
            onClick: () => setSelectedLog(record),
            className: styles.tableRow,
          })}
          onChange={(_, filters) => {
            if (filters.type && Array.isArray(filters.type) && filters.type.length > 0) {
              onFilterChange(filters.type[0] as string);
            } else {
              onFilterChange(null);
            }
          }}
        />
      </div>

      <Drawer title="로그 상세 정보" open={!!selectedLog} onClose={() => setSelectedLog(null)} width={720}>
        {selectedLog &&
          (() => {
            const formatMetaData = (data: unknown): string => {
              if (typeof data === 'string') {
                return data;
              }
              try {
                return JSON.stringify(data, null, 2);
              } catch {
                return '[Circular or Invalid JSON]';
              }
            };

            const formattedResponseMeta = selectedLog.data.responseDataMeta
              ? formatMetaData(selectedLog.data.responseDataMeta)
              : '';

            const formattedRequestMeta = selectedLog.data.requestDataMeta
              ? formatMetaData(selectedLog.data.requestDataMeta)
              : '';

            return (
              <div className={styles.detailContent}>
                <Descriptions title="기본 정보" column={1} bordered size="small">
                  <Descriptions.Item label="ID">{selectedLog.id}</Descriptions.Item>
                  <Descriptions.Item label="타입">
                    {(() => {
                      const config = PERFORMANCE_TYPE_CONFIG[selectedLog.type as keyof typeof PERFORMANCE_TYPE_CONFIG];
                      return config ? <Tag color={config.color}>{config.label}</Tag> : <Tag>{selectedLog.type}</Tag>;
                    })()}
                  </Descriptions.Item>
                  <Descriptions.Item label="시간">
                    {new Date(selectedLog.timestamp).toLocaleString('ko-KR')}
                  </Descriptions.Item>
                  <Descriptions.Item label="URL">{String(selectedLog.data.url || '-')}</Descriptions.Item>
                  <Descriptions.Item label="Method">
                    {String(selectedLog.data.method || '-').toUpperCase()}
                  </Descriptions.Item>
                  <Descriptions.Item label="응답시간">
                    {selectedLog.data.duration ? `${Math.round(selectedLog.data.duration)}ms` : '-'}
                  </Descriptions.Item>
                  {selectedLog.data.status && (
                    <Descriptions.Item label="상태">
                      <Tag color={String(selectedLog.data.status).startsWith('2') ? 'success' : 'error'}>
                        {String(selectedLog.data.status)}
                      </Tag>
                    </Descriptions.Item>
                  )}
                  {selectedLog.data.responseSize && (
                    <Descriptions.Item label="응답 크기">
                      {formatBytes(selectedLog.data.responseSize)}
                    </Descriptions.Item>
                  )}
                  {selectedLog.data.errorCode && (
                    <Descriptions.Item label="에러 코드">{String(selectedLog.data.errorCode)}</Descriptions.Item>
                  )}
                </Descriptions>

                {(selectedLog.data.errorMessage || selectedLog.data.responseMessage) && (
                  <Descriptions title="에러 메시지" column={1} bordered size="small" className={styles.dataSection}>
                    {selectedLog.data.errorMessage && (
                      <Descriptions.Item label="Error Message">
                        <div className={styles.errorMessage}>{String(selectedLog.data.errorMessage)}</div>
                      </Descriptions.Item>
                    )}
                    {selectedLog.data.responseMessage && (
                      <Descriptions.Item label="Response Message">
                        <div className={styles.errorMessage}>{String(selectedLog.data.responseMessage)}</div>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                )}

                {formattedResponseMeta && (
                  <Descriptions title="응답 메타정보" column={1} bordered size="small" className={styles.dataSection}>
                    <Descriptions.Item label="Response Meta">
                      <pre className={styles.jsonContent}>{formattedResponseMeta}</pre>
                    </Descriptions.Item>
                  </Descriptions>
                )}

                {formattedRequestMeta && (
                  <Descriptions title="요청 메타정보" column={1} bordered size="small" className={styles.dataSection}>
                    <Descriptions.Item label="Request Meta">
                      <pre className={styles.jsonContent}>{formattedRequestMeta}</pre>
                    </Descriptions.Item>
                  </Descriptions>
                )}

                {selectedLog.data.responseHeaderKeys && selectedLog.data.responseHeaderKeys.length > 0 && (
                  <Descriptions title="응답 헤더 키" column={1} bordered size="small" className={styles.dataSection}>
                    <Descriptions.Item label="Header Keys">
                      <div className={styles.headerKeys}>
                        {(selectedLog.data.responseHeaderKeys as string[]).map((key) => (
                          <Tag key={key}>{key}</Tag>
                        ))}
                      </div>
                    </Descriptions.Item>
                  </Descriptions>
                )}

                {selectedLog.data.requestHeaderKeys && selectedLog.data.requestHeaderKeys.length > 0 && (
                  <Descriptions title="요청 헤더 키" column={1} bordered size="small" className={styles.dataSection}>
                    <Descriptions.Item label="Header Keys">
                      <div className={styles.headerKeys}>
                        {(selectedLog.data.requestHeaderKeys as string[]).map((key) => (
                          <Tag key={key}>{key}</Tag>
                        ))}
                      </div>
                    </Descriptions.Item>
                  </Descriptions>
                )}

                {selectedLog.data.stack && (
                  <Descriptions title="스택 트레이스" column={1} bordered size="small" className={styles.dataSection}>
                    <Descriptions.Item label="Stack">
                      <pre className={styles.stackTrace}>{String(selectedLog.data.stack)}</pre>
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </div>
            );
          })()}
      </Drawer>
    </>
  );
};
